import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Button } from "./button";
import { CalendarIcon, ChevronDown } from "lucide-react";

interface pickerType {
  onMonthChange?: (month: string) => void;
  onYearChange?: (year: number) => void;
  date?: Date;
  setDate?: (date: Date) => void;
  trigger?: React.ReactNode;
}

export const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const years = Array.from({ length: 9 }, (_, i) => {
  const year = new Date().getFullYear() - 8 + i;
  return year <= new Date().getFullYear() ? year : null;
}).filter((year): year is number => year !== null);

export function CustomMonthPicker({
  onMonthChange,
  onYearChange,
  date = new Date(),
  setDate,
  trigger,
}: pickerType) {
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const handleMonthSelect = (monthIndex: number) => {
    const newDate = new Date(date);
    newDate.setMonth(monthIndex);
    if (setDate) setDate(newDate);

    const monthStr = months[monthIndex];
    if (onMonthChange) {
      onMonthChange(monthStr);
    }
    setOpen(false);
  };

  const handleYearSelect = (year: number) => {
    if (onYearChange) onYearChange(year);
    const newDate = new Date(date);
    newDate.setFullYear(year);
    if (setDate) setDate(newDate);
    setYearDropdownOpen(false);
  };

  const isMonthDisabled = (monthIdx: number) => {
    const now = new Date();
    return (
      date.getFullYear() > now.getFullYear() ||
      (date.getFullYear() === now.getFullYear() && monthIdx > now.getMonth())
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger || (
          <Button
            variant="outline"
            className="w-[160px] justify-start text-left font-normal"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {months[date.getMonth()]}, {date.getFullYear()}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <div className="p-4 BookEZ_card_bg">
          {/* Year Selector */}
          <div className="relative w-full mb-4">
            <Button
              variant="monthPicker"
              size="monthPicker"
              className="w-full justify-between"
              onClick={() => setYearDropdownOpen(!yearDropdownOpen)}
              type="button"
            >
              <span className="BookEZ_font_body_medium">
                {date.getFullYear()}
              </span>
              <ChevronDown
                className={`h-4 w-4 transition-transform ${
                  yearDropdownOpen ? "rotate-180" : ""
                }`}
              />
            </Button>

            {yearDropdownOpen && (
              <div className="absolute mt-1 w-full max-h-[200px] overflow-auto BookEZ_card_bg border BookEZ_card_border_color BookEZ_card_radius shadow-lg z-10">
                <div className="py-1 ">
                  {years.map((year) => (
                    <button
                      key={year}
                      className={`w-full text-left px-4 py-2 BookEZ_font_body_size hover:bg-gray-100
                        ${
                          year === date.getFullYear()
                            ? "bg-gray-100 BookEZ_font_body_medium"
                            : ""
                        }`}
                      onClick={() => handleYearSelect(year)}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Month Grid */}
          <div className="grid grid-cols-3 gap-2">
            {months.map((month, index) => {
              const isSelected = date.getMonth() === index;
              const isDisabled = isMonthDisabled(index);
              return (
                <Button
                  key={month}
                  variant={
                    isDisabled
                      ? "monthPickerDisabled"
                      : isSelected
                      ? "filled"
                      : "monthPicker"
                  }
                  size="monthPicker"
                  onClick={() => !isDisabled && handleMonthSelect(index)}
                  disabled={isDisabled}
                >
                  {month}
                </Button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
